<template>
  <div v-if="groups.length" class="space-y-5 mb-6">
    <div
      v-for="group in groups"
      :key="group.key"
      class="space-y-2"
    >
      <UFormField
        :label="packageGroupPickLabel(group, allowMultiples)"
        required
        :error="errors[group.key]"
      >
        <p class="mb-2 text-xs text-muted">
          {{ pickHint(group) }}
        </p>
        <div class="space-y-2">
          <template v-if="!allowMultiples">
          <button
            v-for="option in group.options"
            :key="option.itemProductId"
            type="button"
            class="flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors"
            :class="isSelected(group.key, option.itemProductId)
              ? 'border-primary-500 bg-primary-50/60 dark:bg-primary-950/30 ring-1 ring-primary-500/40'
              : 'border-default hover:border-primary-300 hover:bg-muted/40'"
            @click="toggleOption(group, option.itemProductId)"
          >
            <div class="h-14 w-12 shrink-0 overflow-hidden rounded-lg bg-muted">
              <AppImage
                v-if="optionImage(option)"
                :src="optionImage(option)"
                :alt="option.itemProduct?.name || ''"
                preset="thumbnail"
                fit="cover"
                width="48"
                height="56"
                sizes="48px"
                class="h-full w-full"
                loading="lazy"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-dimmed">
                <UIcon name="i-heroicons-cube" class="h-5 w-5" />
              </div>
            </div>
            <div class="min-w-0 flex-1">
              <div class="truncate text-sm font-medium text-highlighted">
                {{ option.itemProduct?.name || `Produs #${option.itemProductId}` }}
              </div>
              <div class="mt-0.5 text-xs text-muted">
                <span v-if="option.quantity > 1">{{ option.quantity }} × </span>
                {{ formatPrice(Number(option.packagePrice) || 0) }}
              </div>
            </div>
            <UIcon
              :name="isSelected(group.key, option.itemProductId)
                ? 'i-heroicons-check-circle-solid'
                : 'i-heroicons-check-circle'"
              class="h-5 w-5 shrink-0"
              :class="isSelected(group.key, option.itemProductId)
                ? 'text-primary-600'
                : 'text-dimmed'"
            />
          </button>
          </template>

          <template v-else>
          <div
            v-for="option in group.options"
            :key="`qty-${option.itemProductId}`"
            class="flex w-full items-center gap-3 rounded-xl border px-3 py-2.5"
            :class="optionQty(group.key, option.itemProductId) > 0
              ? 'border-primary-500 bg-primary-50/60 dark:bg-primary-950/30 ring-1 ring-primary-500/40'
              : 'border-default'"
          >
            <div class="h-14 w-12 shrink-0 overflow-hidden rounded-lg bg-muted">
              <AppImage
                v-if="optionImage(option)"
                :src="optionImage(option)"
                :alt="option.itemProduct?.name || ''"
                preset="thumbnail"
                fit="cover"
                width="48"
                height="56"
                sizes="48px"
                class="h-full w-full"
                loading="lazy"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-dimmed">
                <UIcon name="i-heroicons-cube" class="h-5 w-5" />
              </div>
            </div>
            <div class="min-w-0 flex-1">
              <div class="truncate text-sm font-medium text-highlighted">
                {{ option.itemProduct?.name || `Produs #${option.itemProductId}` }}
              </div>
              <div class="mt-0.5 text-xs text-muted">
                <span v-if="option.quantity > 1">{{ option.quantity }} × </span>
                {{ formatPrice(Number(option.packagePrice) || 0) }}
              </div>
            </div>
            <div class="flex shrink-0 items-center gap-1">
              <UButton
                icon="i-heroicons-minus"
                size="xs"
                color="neutral"
                variant="soft"
                :disabled="optionQty(group.key, option.itemProductId) <= 0"
                aria-label="Scade cantitatea"
                @click="adjustQty(group, option.itemProductId, -1)"
              />
              <span class="w-7 text-center text-sm font-medium tabular-nums">
                {{ optionQty(group.key, option.itemProductId) }}
              </span>
              <UButton
                icon="i-heroicons-plus"
                size="xs"
                color="neutral"
                variant="soft"
                :disabled="!canIncrease(group)"
                aria-label="Crește cantitatea"
                @click="adjustQty(group, option.itemProductId, 1)"
              />
            </div>
          </div>
          </template>
        </div>
      </UFormField>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  PACKAGE_MULTI_MAX,
  groupPackageItemsByCategory,
  packageGroupPickLabel,
  packageItemLinePrice,
  type PackageCategoryGroup,
  type ProductPackageCategoryRule,
  type ProductPackageChoiceGroup,
  type ProductPackageItem,
} from '~/utils/productPackage'

const props = defineProps<{
  items: ProductPackageItem[]
  rules?: ProductPackageCategoryRule[]
  choiceGroups?: ProductPackageChoiceGroup[]
  allowMultiples?: boolean
}>()

const emit = defineEmits<{
  change: [selectedItemProductIds: number[], unitPrice: number]
}>()

const { formatPrice } = useSeo()

const allowMultiples = computed(() => props.allowMultiples === true)
const groups = computed(() =>
  groupPackageItemsByCategory(props.items, props.rules, props.choiceGroups, allowMultiples.value),
)
const selections = reactive<Record<string, number[]>>({})
const errors = reactive<Record<string, string>>({})
const ready = ref(false)

function optionImage(option: ProductPackageItem) {
  return option.itemProduct?.images?.find((img) => img.isPrimary)?.url
    || option.itemProduct?.images?.[0]?.url
}

function pickHint(group: PackageCategoryGroup) {
  const count = (selections[group.key] || []).length
  if (allowMultiples.value) {
    const extra = Math.max(0, count - group.minSelect)
    if (count < group.minSelect) {
      return `Selectate: ${count} · minim ${group.minSelect} (poți adăuga extra)`
    }
    if (extra > 0) {
      return `Selectate: ${count} · minim ${group.minSelect} + ${extra} extra`
    }
    return `Selectate: ${count} · minim ${group.minSelect} (poți adăuga extra)`
  }
  const target = group.minSelect === group.maxSelect
    ? group.minSelect
    : group.maxSelect
  return `Selectate: ${count}/${target}`
}

function clearState() {
  for (const key of Object.keys(selections)) {
    delete selections[key]
  }
  for (const key of Object.keys(errors)) {
    delete errors[key]
  }
}

function applyDefaults() {
  for (const group of groups.value) {
    if (!selections[group.key]) selections[group.key] = []
    if (selections[group.key].length > 0) continue
    if (allowMultiples.value) {
      if (group.options.length === group.minSelect) {
        selections[group.key] = group.options.map((row) => row.itemProductId)
      }
      continue
    }
    if (
      group.options.length === group.minSelect
      && group.minSelect === group.maxSelect
    ) {
      selections[group.key] = group.options.map((row) => row.itemProductId)
    }
  }
}

function isSelected(groupKey: string, itemProductId: number) {
  return (selections[groupKey] || []).includes(itemProductId)
}

function optionQty(groupKey: string, itemProductId: number) {
  return (selections[groupKey] || []).filter((id) => id === itemProductId).length
}

function canIncrease(group: PackageCategoryGroup) {
  return (selections[group.key] || []).length < PACKAGE_MULTI_MAX
}

function toggleOption(group: PackageCategoryGroup, itemProductId: number) {
  const current = [...(selections[group.key] || [])]
  const idx = current.indexOf(itemProductId)
  if (idx >= 0) {
    current.splice(idx, 1)
  } else if (group.maxSelect === 1) {
    current.splice(0, current.length, itemProductId)
  } else if (current.length < group.maxSelect) {
    current.push(itemProductId)
  } else {
    // At max: replace oldest selection
    current.shift()
    current.push(itemProductId)
  }
  selections[group.key] = current
  errors[group.key] = ''
  emitChange()
}

function adjustQty(group: PackageCategoryGroup, itemProductId: number, delta: number) {
  const current = [...(selections[group.key] || [])]
  if (delta > 0) {
    if (current.length >= PACKAGE_MULTI_MAX) return
    current.push(itemProductId)
  } else {
    const idx = current.lastIndexOf(itemProductId)
    if (idx < 0) return
    current.splice(idx, 1)
  }
  selections[group.key] = current
  errors[group.key] = ''
  emitChange()
}

function selectedIds(): number[] {
  const ids: number[] = []
  for (const group of groups.value) {
    for (const id of selections[group.key] || []) {
      if (typeof id === 'number' && id > 0) ids.push(id)
    }
  }
  return ids
}

function unitPriceFromSelections(): number {
  const counts = new Map<number, number>()
  for (const id of selectedIds()) {
    counts.set(id, (counts.get(id) || 0) + 1)
  }
  return groups.value.reduce((sum, group) => {
    return sum + group.options.reduce((groupSum, row) => {
      const raw = counts.get(row.itemProductId) || 0
      const qty = allowMultiples.value ? raw : (raw > 0 ? 1 : 0)
      return groupSum + (qty > 0 ? packageItemLinePrice(row) * qty : 0)
    }, 0)
  }, 0)
}

function emitChange() {
  emit('change', selectedIds(), unitPriceFromSelections())
}

function validate(): boolean {
  let ok = true
  for (const group of groups.value) {
    const count = (selections[group.key] || []).length
    if (allowMultiples.value) {
      if (count < group.minSelect) {
        const unitWord = group.minSelect === 1 ? 'bucată' : 'bucăți'
        errors[group.key] = `Alege cel puțin ${group.minSelect} ${unitWord} din „${group.label}"`
        ok = false
      } else {
        errors[group.key] = ''
      }
      continue
    }
    if (count < group.minSelect || count > group.maxSelect) {
      if (group.minSelect === group.maxSelect) {
        errors[group.key] = `Alege ${group.minSelect} ${group.minSelect === 1 ? 'produs' : 'produse'} din „${group.label}"`
      } else {
        errors[group.key] = `Alege între ${group.minSelect} și ${group.maxSelect} produse din „${group.label}"`
      }
      ok = false
    } else {
      errors[group.key] = ''
    }
  }
  return ok
}

function getSelectedIds(): number[] {
  return selectedIds()
}

function syncAfterMount() {
  if (!ready.value) return
  clearState()
  applyDefaults()
  emitChange()
}

onMounted(() => {
  ready.value = true
  applyDefaults()
  emitChange()
})

watch(
  () => [props.items, props.rules, props.choiceGroups, allowMultiples.value] as const,
  () => {
    if (!ready.value) return
    syncAfterMount()
  },
  { deep: true },
)

defineExpose({ validate, getSelectedIds })
</script>
